/**
 * AdminDashboard — landing page for /admin.
 * Displays KPI cards (total, sync'd, queue, drafts) and the InformesTab.
 * Extracted from AdminPanel so each admin section can be a full-page route.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CloudUpload, Plus, Palette, ArrowUpRight } from "lucide-react";
import { listInformes, type EstadoInforme, type Informe } from "@/features/reports/index.js";
import { buttonVariants } from "@/shared/ui/button.js";
import { cn } from "@/shared/lib/utils.js";
import { friendlyMessage } from "@/shared/lib/errors.js";
import { InformesTab } from "./InformesTab.js";
import { MisInformesTab } from "./MisInformesTab.js";

const labelClass =
  "font-mono text-[0.625rem] font-medium uppercase tracking-[0.18em] text-muted-foreground";

const ESTADO_DOT: Record<EstadoInforme, string> = {
  borrador: "var(--estado-borrador)",
  en_cola: "var(--estado-en-cola)",
  sincronizado: "var(--estado-sincronizado)",
  enviado: "var(--estado-enviado)",
};

function MiniKpi({
  label,
  value,
  estado,
}: {
  label: string;
  value: number | string;
  estado: EstadoInforme;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-3 flex items-center gap-2">
        <span
          aria-hidden
          className="size-2 rounded-full"
          style={{ backgroundColor: ESTADO_DOT[estado] }}
        />
        <span className={labelClass}>{label}</span>
      </div>
      <div className="font-display text-3xl font-semibold leading-none text-foreground">
        {value}
      </div>
    </div>
  );
}

type InformesView = "todos" | "mis";

export function AdminDashboard() {
  const navigate = useNavigate();
  const [informes, setInformes] = useState<Informe[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [informesView, setInformesView] = useState<InformesView>("todos");

  const cargarKpis = useCallback(async () => {
    setError(null);
    try {
      setInformes(await listInformes());
    } catch (err) {
      setError(friendlyMessage(err));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargarKpis();
  }, [cargarKpis]);

  const total = informes.length;
  const sinc = useMemo(
    () => informes.filter((i) => i.estado === "sincronizado").length,
    [informes],
  );
  const cola = useMemo(
    () => informes.filter((i) => i.estado === "en_cola").length,
    [informes],
  );
  const borr = useMemo(
    () => informes.filter((i) => i.estado === "borrador").length,
    [informes],
  );
  const pct = total ? Math.round((sinc / total) * 100) : 0;

  return (
    <main className="mx-auto w-full max-w-[1440px] space-y-8 px-6 py-8 md:px-8">
      {/* ── Title block ──────────────────────────────────────────────── */}
      <div
        className="forja-rise flex flex-wrap items-end justify-between gap-4"
        style={{ animationDelay: "60ms" }}
      >
        <div className="space-y-2">
          <p className={labelClass}>Administración</p>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
            Panel de{" "}
            <span className="relative -top-0.5 inline-block -rotate-2 rounded-full bg-brand-accent px-3 py-0.5 italic font-medium text-brand-accent-foreground">
              administración
            </span>
          </h1>
          <p className="max-w-xl text-sm text-muted-foreground">
            Gestiona informes de inspección, usuarios y clientes desde un solo lugar.
          </p>
        </div>
        <Link
          to="/informe/nuevo"
          className={cn(
            buttonVariants(),
            "gap-2 bg-brand-accent font-mono text-xs font-semibold uppercase tracking-[0.12em] text-brand-accent-foreground hover:bg-brand-accent/90",
          )}
        >
          <Plus className="size-4" aria-hidden />
          Nuevo informe
        </Link>
      </div>

      {/* ── KPI cluster ──────────────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-4">
        <div className="forja-rise rounded-xl border border-border bg-card p-6">
          <div className="mb-6 flex items-center gap-2">
            <span aria-hidden className="size-2 rounded-full bg-foreground" />
            <span className={labelClass}>Total informes</span>
          </div>
          <div className="font-display text-5xl font-semibold leading-none text-foreground">
            {cargando ? "—" : total}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">en el sistema</p>
        </div>

        <div className="forja-rise rounded-xl border border-[var(--brand-accent)]/100 bg-[var(--brand-accent)]/5 p-6">
          <div className="mb-6 flex items-center gap-2">
            <span
              aria-hidden
              className="size-2 rounded-full"
              style={{ backgroundColor: "var(--brand-accent)" }}
            />
            <span className={labelClass}>Sincronizados</span>
          </div>
          <div className="flex items-end justify-between gap-2">
            <span className="font-display text-5xl font-semibold leading-none text-foreground">
              {cargando ? "—" : sinc}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-background/70 px-2 py-1 font-mono text-[0.625rem] font-semibold text-foreground">
              <CloudUpload className="size-3" aria-hidden /> {pct}%
            </span>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">listos para enviar</p>
        </div>

        <div className="grid grid-cols-2 gap-4 lg:col-span-2">
          <MiniKpi label="En cola" value={cargando ? "—" : cola} estado="en_cola" />
          <MiniKpi label="Borradores" value={cargando ? "—" : borr} estado="borrador" />
          <button
            type="button"
            onClick={() => navigate("/admin/manual")}
            className="col-span-2 flex items-center justify-between gap-4 rounded-xl bg-foreground p-5 text-left text-background transition-colors hover:bg-foreground/90"
          >
            <div>
              <p className="font-display text-base font-semibold leading-tight">
                ¿No sabes por dónde empezar?
              </p>
              <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-background/15 px-3 py-1 font-mono text-[0.625rem] uppercase tracking-[0.12em]">
                Ver manual de usuario <ArrowUpRight className="size-3" aria-hidden />
              </span>
            </div>
            <Palette className="size-8 shrink-0 text-background/40" aria-hidden />
          </button>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* ── Informes section ─────────────────────────────────────────────── */}
      <div className="forja-rise space-y-4 lg:col-span-3" style={{ animationDelay: "80ms" }}>
        {/* Toggle: Todos / Mis informes */}
        <div className="flex items-center gap-2">
          <div
            role="tablist"
            aria-label="Vista de informes"
            className="inline-flex gap-1 rounded-full border border-[#ddd9cc] bg-card p-1"
          >
            <button
              type="button"
              role="tab"
              aria-selected={informesView === "todos"}
              onClick={() => setInformesView("todos")}
              className={cn(
                "rounded-full px-4 py-1.5 font-mono text-[0.625rem] font-medium uppercase tracking-[0.12em] transition-colors",
                informesView === "todos"
                  ? "bg-brand-accent text-brand-accent-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Todos
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={informesView === "mis"}
              onClick={() => setInformesView("mis")}
              className={cn(
                "rounded-full px-4 py-1.5 font-mono text-[0.625rem] font-medium uppercase tracking-[0.12em] transition-colors",
                informesView === "mis"
                  ? "bg-brand-accent text-brand-accent-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Mis informes
            </button>
          </div>
        </div>

        {informesView === "todos" ? (
          <InformesTab onChange={cargarKpis} />
        ) : (
          <MisInformesTab onChange={cargarKpis} />
        )}
      </div>
    </main>
  );
}
